<template>
    <BottomSheet persist-key="ai-panel" :expanded-snap-point="0.45" :is-hidden="isEditing" :expand-requests="expandRequests">
        <!-- AI panel content (hidden at collapsed snap point) -->
        <div ref="scrollableRef" class="overflow-y-auto overscroll-contain" style="max-height: 45vh">
            <AiPanelBody />
        </div>

        <template #footer>
            <div class="flex flex-row-reverse items-center gap-4 px-4 py-3">
                <div v-if="price" class="shrink-0">
                    <span class="text-2xl font-extrabold" v-text="formatPrice(price)" />
                    <span v-if="recommendedPrice && recommendedPrice > 0 && recommendedPrice > price" class="flex items-center gap-1 text-sm">
                        <span v-text="$t('general.prices.from.label')" />
                        <span v-text="formatPrice(recommendedPrice)" data-test-id="recommended-price" class="line-through" />
                    </span>
                </div>

                <!--
                    min-h-14 reserves the order button's own height, so the footer keeps one height
                    whether the button is there, replaced by the save-text hint, or absent entirely
                    mid-generation — the sheet is parked on top of it and would otherwise shift.
                -->
                <div class="flex min-h-14 min-w-0 flex-1 items-center">
                    <AiUploadButton hide-until-text-saved class="w-full" />
                </div>
            </div>
        </template>
    </BottomSheet>
</template>

<script setup lang="ts">
import BottomSheet from '@/components/sheets/BottomSheet.vue';
import { useAiEditorMode } from '@/composables/ai-upload/useAiEditorMode';
import { useAiSheetExpansion } from '@/composables/ai-upload/useAiSheetExpansion';
import { useFormatPrice } from '@/composables/useFormatPrice';
import AiPanelBody from '@/pages/ai-product-page/partials/ai-panel/AiPanelBody.vue';
import AiUploadButton from '@/pages/ai-product-page/partials/ai-upload/AiUploadButton.vue';
import { useProductStore } from '@/stores/products/ProductStore';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';

const formatPrice = useFormatPrice();
const productStore = useProductStore();
const { isEditing } = useAiEditorMode();
const { expandRequests } = useAiSheetExpansion();

const price = computed(() => productStore.selectedProduct.product.data.product_price ?? undefined);
const recommendedPrice = computed(() => productStore.selectedProduct.product.data.product_price_recommended ?? undefined);

const scrollableRef = ref<HTMLElement | undefined>(undefined);
let mutationObserver: MutationObserver | undefined;

onMounted(() => {
    if (!scrollableRef.value) {
        return;
    }

    mutationObserver = new MutationObserver(() => {
        if (window.innerWidth >= 768) {
            return;
        }

        nextTick(() => {
            scrollableRef.value?.scrollTo({ top: scrollableRef.value.scrollHeight, behavior: 'smooth' });
        });
    });

    mutationObserver.observe(scrollableRef.value, { childList: true, subtree: true });
});

onUnmounted(() => mutationObserver?.disconnect());
</script>
